import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Gift, Users, Copy, Share2, Check, 
  Coins, Trophy, ArrowRight, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Referrals() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  
  const [referralData, setReferralData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      fetchReferralData();
    }
  }, [user, authLoading, navigate]);

  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [codeRes, referralsRes] = await Promise.all([
        axios.get(`${API}/referral/my-code`, { headers }),
        axios.get(`${API}/referral/my-referrals`, { headers })
      ]);
      
      setReferralData(codeRes.data);
      setReferrals(referralsRes.data.referrals || []);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error('Could not load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (referralData?.referral_link) {
      await navigator.clipboard.writeText(referralData.referral_link);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyCode = async () => {
    if (referralData?.referral_code) {
      await navigator.clipboard.writeText(referralData.referral_code);
      toast.success('Code copied!');
    }
  };

  const shareLink = async () => {
    if (navigator.share && referralData?.referral_link) {
      try {
        await navigator.share({
          title: 'Join Ella Gold',
          text: 'Join me on Ella Gold and get 5 free tokens!',
          url: referralData.referral_link
        });
      } catch (err) {
        copyLink();
      }
    } else {
      copyLink();
    }
  };

  const handleApplyCode = async () => {
    if (!applyCode.trim()) return;
    
    setApplying(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/referral/apply?referral_code=${applyCode.trim().toUpperCase()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(res.data.message);
      setApplyCode('');
      fetchReferralData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not apply code');
    } finally {
      setApplying(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-[#D4AF37]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-sm mb-4">
            <Gift className="w-4 h-4" />
            Referral Program
          </div>
          <h1 className="text-4xl font-medium mb-4">
            Invite Friends, <span className="brand-gold">Earn Tokens</span>
          </h1>
          <p className="text-gray-400 max-w-lg mx-auto">
            Share your unique link and earn {referralData?.bonus_per_referral || 10} tokens for every friend who joins!
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="glass rounded-xl p-6 text-center border border-white/10">
            <Users className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
            <div className="text-3xl font-bold text-white mb-1">{referralData?.total_referrals || 0}</div>
            <div className="text-sm text-gray-400">Friends Invited</div>
          </div>
          <div className="glass rounded-xl p-6 text-center border border-white/10">
            <Coins className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
            <div className="text-3xl font-bold text-white mb-1">{referralData?.total_earned || 0}</div>
            <div className="text-sm text-gray-400">Tokens Earned</div>
          </div>
          <div className="glass rounded-xl p-6 text-center border border-white/10">
            <Trophy className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
            <div className="text-3xl font-bold text-white mb-1">{referralData?.bonus_per_referral || 10}</div>
            <div className="text-sm text-gray-400">Tokens Per Referral</div>
          </div>
        </div>

        {/* Referral Link Section */}
        <div className="glass rounded-xl p-6 mb-8 border border-[#D4AF37]/30">
          <h2 className="text-xl font-medium mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D4AF37]" />
            Your Referral Link
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="flex-1 relative">
              <Input 
                value={referralData?.referral_link || ''}
                readOnly
                className="bg-white/5 border-white/10 text-white pr-24"
              />
              <button
                onClick={copyLink}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 transition-colors"
              >
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-[#D4AF37]" />}
              </button>
            </div>
            <Button onClick={shareLink} className="bg-[#D4AF37] hover:bg-[#B8963A] text-black">
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
            <div className="text-sm text-gray-400">Your Code:</div>
            <div 
              onClick={copyCode}
              className="font-mono text-xl text-[#D4AF37] cursor-pointer hover:text-[#B8963A] transition-colors"
            >
              {referralData?.referral_code || '------'}
            </div>
            <button onClick={copyCode} className="text-gray-400 hover:text-white">
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* How It Works */}
        <div className="glass rounded-xl p-6 mb-8 border border-white/10">
          <h2 className="text-xl font-medium mb-6">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-[#D4AF37] font-bold">1</span>
              </div>
              <h3 className="font-medium mb-1">Share Your Link</h3>
              <p className="text-sm text-gray-400">Send your unique link to friends</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-[#D4AF37] font-bold">2</span>
              </div>
              <h3 className="font-medium mb-1">They Sign Up</h3>
              <p className="text-sm text-gray-400">Your friend creates an account</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-[#D4AF37] font-bold">3</span>
              </div>
              <h3 className="font-medium mb-1">You Both Earn</h3>
              <p className="text-sm text-gray-400">You get {referralData?.bonus_per_referral || 10} tokens, they get 5!</p>
            </div>
          </div>
        </div>

        {/* Apply Code Section */}
        <div className="glass rounded-xl p-6 mb-8 border border-white/10">
          <h2 className="text-xl font-medium mb-4">Have a Referral Code?</h2>
          <div className="flex gap-3">
            <Input 
              value={applyCode}
              onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
              placeholder="Enter code (e.g. ABC12345)"
              className="bg-white/5 border-white/10 text-white uppercase"
              maxLength={8}
            />
            <Button 
              onClick={handleApplyCode}
              disabled={applying || !applyCode.trim()}
              className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
            >
              {applying ? 'Applying...' : 'Apply'}
            </Button>
          </div>
        </div>

        {/* Referrals List */}
        {referrals.length > 0 && (
          <div className="glass rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-medium mb-4">Your Referrals</h2>
            <div className="space-y-3">
              {referrals.map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <div className="font-medium">{ref.name || 'Anonymous'}</div>
                      <div className="text-sm text-gray-400">{ref.email}</div>
                    </div>
                  </div>
                  <div className="text-[#D4AF37] font-medium">+{referralData?.bonus_per_referral || 10} tokens</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-12">
          <Button 
            onClick={() => navigate('/tokens')}
            variant="outline"
            className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
          >
            Buy More Tokens
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
